import { useGetSettingsPermissionsEmployees } from '@agos/api-client/auth'
import { effectiveRoleKey, T, tt } from '@agos/shared'
import { cn, FilterSelect, Spinner, StatusBadge } from '@agos/ui'
import { Search } from 'lucide-react'
import { useState } from 'react'

import { roleRefusalCode, useRolesOverview } from '../../roles/roles-api'
import { orderRoles, peopleRows, roleNameOf, type Navigate, type Place } from '../role-model'
import { RoleChip } from '../RoleChip'
import { NotManaged, TabReadFailed } from '../TabReadFailed'
import { PersonPanel } from './PersonPanel'

/**
 * *Ľudia* - the active people in scope, and one person's panel at `?person=id`. `FRONTEND.md` §9.752.
 *
 * **What the *Výnimky* tab was, grown around the person**: the list keeps §9.238's search and the exceptions-first
 * order, and each person now carries their role chip and *+ rozvoz*; the panel holds the role picker, *Čo smie a
 * prečo* and today's exceptions editor, moved unchanged with its `data-override-*` markers.
 *
 * **A delegate sees the people whose role they may assign, and no exceptions**: those stay the main administrator's
 * (owner question #165), so their read - `GET /settings/permissions/employees`, main administrator only - is not even
 * asked for a delegate.
 *
 * Two reads, two failures: the list's own (`people`) replaces the tab, and the exceptions read (`overrides`) replaces
 * only the sections that need it, so a failed exceptions read does not take the role picker away.
 */
export function PeopleTab({ mainAdmin, place, navigate }: { mainAdmin: boolean; place: Place; navigate: Navigate }) {
  const overview = useRolesOverview()
  const rights = useGetSettingsPermissionsEmployees({ query: { enabled: mainAdmin } })
  /**
   * §9.238 - legacy's employee search on this tab, kept: name, position or role (the overview sends no e-mail).
   */
  const [search, setSearch] = useState('')
  const [exceptionsOnly, setExceptionsOnly] = useState(false)

  if (overview.isError) {
    if (roleRefusalCode(overview.error) === 'ROLE_NOT_MANAGED') return <NotManaged />
    return <TabReadFailed what="people" retry={() => void overview.refetch()} />
  }

  if (overview.isLoading || overview.data === undefined) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  const data = overview.data
  const roles = orderRoles(data.roles)
  const nameOf = (key: string) => roleNameOf(data.roles, key)
  const rows = peopleRows(data.people, { role: place.role, exceptionsOnly, search }, nameOf)
  const selected = data.people.find((person) => person.id === place.person) ?? null
  const roleOptions = [
    { value: '', label: tt('Všetky role') },
    ...roles
      .filter((role) => data.people.some((person) => effectiveRoleKey(person) === role.key))
      .map((role) => ({
        value: role.key,
        label: nameOf(role.key),
        count: data.people.filter((person) => effectiveRoleKey(person) === role.key).length,
      })),
  ]

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-people-tab>
      <div className="space-y-1.5" data-override-employees>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-employee-search
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tt('Hľadať zamestnanca…')}
            aria-label={tt('Hľadať zamestnanca…')}
            className="w-full pl-9 pr-3 py-2 mb-1.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5 pb-1">
          <FilterSelect
            value={place.role}
            onChange={(value) => navigate({ role: value }, { replace: true })}
            options={roleOptions}
            label={tt('Rola')}
            testId="data-people-role-filter"
          />
          {mainAdmin && (
            <button
              type="button"
              onClick={() => setExceptionsOnly((current) => !current)}
              aria-pressed={exceptionsOnly}
              data-people-exceptions-only={exceptionsOnly}
              className={cn(
                'px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-colors',
                exceptionsOnly
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {tt('Len s výnimkami')}
            </button>
          )}
        </div>
        {rows.length === 0 && (
          <p className="text-xs text-muted-foreground px-1 py-3" data-no-employees>
            <T>Nikto nezodpovedá hľadaniu.</T>
          </p>
        )}
        {rows.map((person) => {
          const roleKey = effectiveRoleKey(person)
          return (
            <button
              key={person.id}
              type="button"
              data-employee={person.id}
              data-override-employee={person.id}
              onClick={() => navigate({ person: person.id })}
              className={cn(
                'w-full flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-colors',
                person.id === place.person ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50',
              )}
            >
              <span className="min-w-0 flex-1 space-y-0.5">
                <span className="block text-sm font-semibold truncate">{person.name}</span>
                <span className="flex flex-wrap items-center gap-1">
                  <RoleChip roleKey={roleKey} roles={data.roles} />
                  {person.alsoDrives && (
                    <span className="text-[10px] font-semibold text-amber-500" data-person-also-drives>
                      + {tt('rozvoz')}
                    </span>
                  )}
                  {(person.position ?? '').trim() !== '' && (
                    <span className="text-[11px] text-muted-foreground truncate">{person.position}</span>
                  )}
                </span>
              </span>
              {mainAdmin && person.overrideCount > 0 && (
                <StatusBadge label={String(person.overrideCount)} color="amber" />
              )}
            </button>
          )
        })}
      </div>

      <div className="lg:col-span-2 space-y-3">
        {selected === null ? (
          <p className="text-xs text-muted-foreground py-8 text-center">
            <T>Vyberte zamestnanca.</T>
          </p>
        ) : (
          <PersonPanel
            key={selected.id}
            person={selected}
            overview={data}
            mainAdmin={mainAdmin}
            rights={rights}
          />
        )}
      </div>
    </div>
  )
}
